import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useToast} from './use-toast'
import {useAddToCartActionContext, useAddToCartModalContext} from './use-add-to-cart-modal'
import {useBonusProductsModalContext} from './use-bonus-products-modal'
import {trackAddProductSetToCart, trackAddToCart} from '../tracking/analytics'
import {useIntl} from 'react-intl'
import {API_ERROR_MESSAGE, MAX_BONUS_PRODUCT_QUANTITY} from '../constants'
import {useCurrentBasket} from './use-current-basket'
import useEinstein from './use-einstein'
import {useDerivedProduct} from './use-derived-product'
import {useProductSpecialOffers} from '../components/special-offer/product-special-offers'
import {useSpecialOffer} from '../components/special-offer'
import {getDisplayPrice, getProductAddToBasketItems, isProductSet} from '../utils/product-utils'
import useEffectEvent from './use-effect-event'
import useProductItemsDetail from './useProductItemsDetail'
import {useStokkeAddItemToBasketMutation} from './useStokkeHelpers'
import {trackInteractionBundleAddedToCart} from '../tracking/interaction-studio'
import {useCurrentCustomer} from './use-current-customer'

/**
 * Add to cart hook implementation - do not use directly, this is used internally by useAddToCart
 * @param {object} product - product to add to cart
 * @param {boolean} fromOldPage - flag if the product is added from the old page
 * @param {boolean} [isProductLoading] - loading state of the product, deprecated
 * @param {number} [quantity] - quantity of the product to add to cart
 * @param {boolean} [showAddToCartModal] - show add to cart modal after adding the product
 * @param {function} [onFinished] - callback after the product is added to the cart and the modals are closed
 * @param {function} [onRecommendedProductClick] - callback when a recommended product on add to cart modal is clicked
 * @param {boolean} [includeInAddToCartModal] - add the product to the list of products in the next add to cart modal -
 *   used by cart bonus product to skip adding the product to the modal as they are added without a modal
 *   and linger until the next regular product is added to cart
 * @return {{isLoading: boolean, handleAddToCart: function({onAfterAddToCart?: function, engraveText?: string}=): Promise<void>}}
 */
export const useAddToCartImplementation = (
    product,
    {
        fromOldPage,
        isLoading: isProductLoading,
        quantity,
        showAddToCartModal = true,
        onFinished,
        onRecommendedProductClick,
        includeInAddToCartModal = true
    } = {}
) => {
    const showToast = useToast()
    const isProductASet = isProductSet(product)
    const {isD2BCustomer, isCustomNetPriceEnabled} = useCurrentCustomer().data
    const {formatMessage} = useIntl()
    const {data: basket} = useCurrentBasket()
    const {data: productItemsDetail} = useProductItemsDetail(basket)
    const einstein = useEinstein()
    const {variant, isOrderable, setIncludesBonusProduct} = useDerivedProduct(product)
    // marks that bonus product modal should be displayed after the bonus products are loaded
    const [isShowBonusProductModal, setIsShowBonusProductModal] = useState(false)
    // activates loading of the bonus products of particular product
    // - tracked separately from showing the modal to avoid reloading on second add to cart
    // - the value is the product id for which to load the bonus product, as using effect to reset on product change leaves
    //   a render cycle where the bonus product loading is triggered for the new product
    const [bonusProductLoadEnabledFor, setBonusProductLoadEnabledFor] = useState(null)
    const [addedProductsToCart, setAddedProductsToCart] = useState(false)
    // use a ref to access the latest value of fromOldPage from handleAddToCart after the async call
    const fromOldPageRef = useRef(fromOldPage)
    fromOldPageRef.current = fromOldPage

    // keep track of basket loading state
    const {isInitialLoading: isBasketLoading} = useCurrentBasket()

    // get all promotions available for the product
    const {promotions, loading: productSpecialOffersLoading} = useProductSpecialOffers(
        product,
        null,
        null
    )

    // compute the bonus promotion for the product
    const bonusPromotion = useMemo(() => {
        return promotions?.find((p) => p?.c_discountProducts?.length > 0)
    }, [promotions])

    // load the bonus products for the bonus promotion when activated for the current product
    const {
        products: bonusProducts,
        colorScheme,
        fontColor
    } = useSpecialOffer(bonusPromotion, bonusProductLoadEnabledFor === product?.id)

    // combined loading state
    const isLoading = isBasketLoading || isProductLoading || productSpecialOffersLoading

    // add to cart modal context
    const {onOpen: onAddToCartModalOpen, data, addProduct} = useAddToCartModalContext()

    // bonus product modal context
    const {isOpen: isBonusProductsModalOpen, onOpen: onBonusProductsModalOpen} =
        useBonusProductsModalContext()

    // clear the flag to show the bonus product modal when it is closed
    useEffect(() => {
        if (!isBonusProductsModalOpen) {
            setIsShowBonusProductModal(false)
        }
    }, [isBonusProductsModalOpen])

    // show bonus product modal when the bonus products are loaded and the flag is set
    const onShowBonusProductModal = useEffectEvent(() => {
        if (fromOldPage) {
            onFinished?.()
            return
        }
        onBonusProductsModalOpen({
            messages: [
                quantity > 1 && {
                    background: 'stokkeBeige.mid',
                    color: 'stokkeBeige.accessibleText',
                    text: formatMessage({
                        defaultMessage: 'You can adjust your bonus product selection in the cart.',
                        id: 'product.alert.bonus_product_selection.adjust_in_cart'
                    })
                },
                quantity > MAX_BONUS_PRODUCT_QUANTITY && {
                    background: 'stokkePink.mid',
                    color: 'stokkePink.dark',
                    text: formatMessage(
                        {
                            defaultMessage:
                                'Maximum {limit, plural, one {# product} other {# products}} can receive a bonus product per promotion.',
                            id: 'product.alert.bonus_product_selection.promotion_limit'
                        },
                        {
                            limit: MAX_BONUS_PRODUCT_QUANTITY
                        }
                    )
                }
            ].filter(Boolean),
            products: bonusProducts?.data,
            bonusProductVariationsMap: bonusPromotion?.c_discountVariantProducts,
            colorScheme,
            fontColor,
            includeInAddToCartModal,
            showAddToCartModal: false,
            setSelectedItem: () => {}
        })
    })
    useEffect(() => {
        if (isShowBonusProductModal && bonusProducts) {
            onShowBonusProductModal()
        }
    }, [isShowBonusProductModal, bonusProducts, onShowBonusProductModal])

    // show add to cart overlay when bonus product modal is closed or not requested and there are products added
    const onShowAddToCartModal = useEffectEvent(() => {
        if (fromOldPage) {
            onFinished?.()
            return
        }
        if (data) {
            onAddToCartModalOpen({
                onRecommendedProductClick,
                onClose: () => {
                    setAddedProductsToCart(false)
                    onFinished?.()
                }
            })
        }
    })
    useEffect(() => {
        if (showAddToCartModal && !isShowBonusProductModal && addedProductsToCart) {
            onShowAddToCartModal()
        }
    }, [addedProductsToCart, isShowBonusProductModal, showAddToCartModal, onShowAddToCartModal])

    const showError = () => {
        showToast({
            title: formatMessage(API_ERROR_MESSAGE),
            status: 'error'
        })
    }

    const addItemToBasketMutation = useStokkeAddItemToBasketMutation()
    const handleAddToCart = async ({onAfterAddToCart, engraveText} = {}) => {
        try {
            if (!isOrderable || isLoading) return
            const productItems = getProductAddToBasketItems(product, engraveText, quantity)
            if (!productItems?.length) return

            let updatedBasket
            try {
                updatedBasket = await addItemToBasketMutation.mutateAsync({productItems})
            } catch (error) {
                try {
                    const errorResponse = await error?.response.json()
                    switch (errorResponse?.type) {
                        case 'https://api.commercecloud.salesforce.com/documentation/error/v1/errors/product-item-not-available':
                            showToast({
                                title: isProductSet(product)
                                    ? formatMessage({
                                          defaultMessage:
                                              'Please refresh your page as stock levels has changed.',
                                          id: 'product.alert.set.stock_levels_changed'
                                      })
                                    : isD2BCustomer
                                    ? product.inventory?.ats != null
                                        ? formatMessage(
                                              {
                                                  defaultMessage:
                                                      'Maximum {quantity, plural, one {# item} other {# items}} in stock. Please contact customer service to place an order for a higher quantity.',
                                                  id: 'product.alert.d2b.max_order_quantity_count'
                                              },
                                              {
                                                  quantity: product.inventory.ats
                                              }
                                          )
                                        : formatMessage({
                                              defaultMessage:
                                                  'Maximum availability in stock. Please contact customer service to place an order for a higher quantity.',
                                              id: 'product.alert.d2b.max_order_quantity'
                                          })
                                    : formatMessage({
                                          defaultMessage:
                                              'Maximum order quantity has been reached.',
                                          id: 'product.alert.b2c.max_order_quantity'
                                      }),
                                status: 'error',
                                duration: 5000
                            })
                            return
                    }
                } catch (e) {
                    console.debug('Error response not available', e)
                }
                // rethrow error if not handled
                throw error
            }

            if (!fromOldPageRef.current) {
                // add current product to add to cart modal
                if (includeInAddToCartModal) {
                    addProduct(product, quantity)
                }
                setAddedProductsToCart(true)
                if (onAfterAddToCart) {
                    onAfterAddToCart()
                }
            }
            // einstein
            einstein.sendAddToCart(
                productItems.map((item) => ({
                    ...item,
                    masterId: product?.master?.masterId
                }))
            )
            // analytics
            const enrichedBasket = {
                ...updatedBasket,
                productItems: updatedBasket.productItems.map((item) => ({
                    ...item,
                    salePrice: item.adjustedPrice || item.price
                }))
            }
            if (isProductASet) {
                const enrichedProduct = {
                    ...product,
                    setProducts: product.setProducts.map((setProduct) => {
                        const lineItem = product.productLineItems?.find(
                            (item) => item.variantId === setProduct.id
                        )
                        return {
                            ...setProduct,
                            salePrice: lineItem?.adjustedPrice || lineItem?.price
                        }
                    })
                }
                trackAddProductSetToCart(enrichedProduct, enrichedBasket, productItemsDetail)
                product?.productLineItems?.forEach((lineItem) => {
                    trackInteractionBundleAddedToCart(lineItem)
                })
            } else {
                const {displayPrice: salePrice} = getDisplayPrice(
                    product,
                    false,
                    isCustomNetPriceEnabled
                )
                trackAddToCart(
                    {
                        ...product,
                        productName: variant?.name || product?.name,
                        salePrice: salePrice
                    },
                    enrichedBasket,
                    productItemsDetail
                )
            }

            if (!fromOldPageRef.current) {
                // if product has bonus product promotions show bonus products modal first - then add to cart modal
                const variationKey = product.master ? product.master.masterId : product.id
                const isCurrentProductBonus =
                    bonusPromotion?.c_discountVariantProducts?.[variationKey]?.indexOf(
                        product.id
                    ) >= 0
                // if we have multiple promotions for the product - use only first one and only in case it is already applied to the basket
                const appliedBonusPromotions = updatedBasket?.bonusDiscountLineItems?.filter(
                    (bonus) => bonus.promotionId === bonusPromotion?.id
                )
                if (
                    appliedBonusPromotions &&
                    bonusPromotion &&
                    !isBonusProductsModalOpen &&
                    !isCurrentProductBonus &&
                    !setIncludesBonusProduct
                ) {
                    setBonusProductLoadEnabledFor(product.id)
                    setIsShowBonusProductModal(true)
                } else if (!showAddToCartModal) {
                    onFinished?.()
                }
            } else {
                onFinished?.()
            }
        } catch (error) {
            console.error(error)
            showError()
        }
    }

    return {
        handleAddToCart,
        isLoading
    }
}

/**
 * Hook to create a function to add a product to the cart showing bonus product modal if available and add to cart modal
 * at the end
 * @param {object} product - product to add to cart
 * @param {boolean} [isLoading] - loading state of the product, does not execute the add to cart if true
 * @param {number} [quantity] - quantity of the product to add to cart
 * @param {boolean} [showAddToCartModal] - show add to cart modal after adding the product. Note the product will be included
 *   in the next displayed add to cart modal unless includeInAddToCartModal is set to false
 * @param {function} [onRecommendedProductClick] - callback when a recommended product on add to cart modal is clicked
 * @param {boolean} [includeInAddToCartModal] - add the product to the list of products in the next add to cart modal -
 *   used by cart bonus product to skip adding the product to the modal as they are added without a modal
 *   and linger until the next regular product is added to cart
 * @return {{isLoading: boolean, handleAddToCart: function({onAfterAddToCart?: function, engraveText?: string}=): Promise<void>}} */
export const useAddToCart = (
    product,
    {
        isLoading,
        quantity = 1,
        showAddToCartModal = true,
        onRecommendedProductClick,
        includeInAddToCartModal = true
    } = {}
) => {
    const {addProduct} = useAddToCartActionContext()
    const {setFinalFocusRef} = useAddToCartModalContext()

    const handleAddToCart = useCallback(
        ({onAfterAddToCart, engraveText, finalFocusRef} = {}) => {
            if (isLoading) {
                return
            }

            setFinalFocusRef(finalFocusRef)
            return addProduct({
                engraveText,
                product,
                quantity,
                includeInAddToCartModal,
                showAddToCartModal,
                onAfterAddToCart,
                onRecommendedProductClick
            })
        },
        [
            addProduct,
            isLoading,
            product,
            quantity,
            includeInAddToCartModal,
            showAddToCartModal,
            onRecommendedProductClick,
            setFinalFocusRef
        ]
    )

    return useMemo(
        () => ({
            isLoading,
            handleAddToCart
        }),
        [isLoading, handleAddToCart]
    )
}
